<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Pagination from 'dashboard/components/table/Pagination.vue';
import ReportDataTable from '../ReportDataTable.vue';
import ReportEmptyState from '../ReportEmptyState.vue';
import AgentCell from './AgentCell.vue';
import { renderLiveCount, sortByOpenThenName } from './liveTableHelpers';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// UI Settings key for agent table page size
const AGENT_TABLE_PAGE_SIZE_KEY = 'report_overview_agent_table_page_size';
const MIN_PAGE_SIZE = 10;

// Get the saved page size from UI settings or default to 10
const getPageSize = () => {
  return uiSettings.value[AGENT_TABLE_PAGE_SIZE_KEY] || MIN_PAGE_SIZE;
};

const handlePageSizeChange = pageSize => {
  updateUISettings({ [AGENT_TABLE_PAGE_SIZE_KEY]: pageSize });
};

const getAgentMetrics = id =>
  props.agentMetrics.find(metrics => metrics.assignee_id === Number(id)) || {};

const tableData = computed(() =>
  props.agents
    .map(agent => {
      const metric = getAgentMetrics(agent.id);
      return {
        agent: agent.available_name || agent.name,
        email: agent.email,
        thumbnail: agent.thumbnail,
        open: metric.open || 0,
        unattended: metric.unattended || 0,
        status: agent.availability_status,
      };
    })
    .sort(sortByOpenThenName)
);

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: renderLiveCount,
    size: 120,
    meta: { align: 'end' },
  }),
  columnHelper.accessor('unattended', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: renderLiveCount,
    size: 140,
    meta: { align: 'end' },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});

const showSkeleton = computed(() => props.isLoading && !tableData.value.length);
const showPagination = computed(() => tableData.value.length > MIN_PAGE_SIZE);
</script>

<template>
  <div class="flex min-w-0 flex-1 flex-col">
    <div
      v-if="showSkeleton"
      class="flex flex-col gap-3 px-5 pb-5 pt-2"
      role="status"
      :aria-label="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')"
    >
      <div
        v-for="item in 4"
        :key="item"
        class="h-10 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
      />
    </div>
    <ReportEmptyState
      v-else-if="!agents.length"
      icon="i-hugeicons-user-multiple"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
    <template v-else>
      <ReportDataTable
        :table="table"
        min-width-class="min-w-[28rem]"
        scroll-class="max-h-[30rem] overflow-y-auto"
      />
      <Pagination
        v-if="showPagination"
        class="border-t border-n-weak px-5 py-3 dark:border-n-strong"
        :table="table"
        show-page-size-selector
        :default-page-size="getPageSize()"
        @page-size-change="handlePageSizeChange"
      />
    </template>
  </div>
</template>
